import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import StyledDrawer from "../StyledDrawer";
import { Button, ButtonProps } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { SheetClose } from "../ui/sheet";
import PrintButton from "./PrintButton";
import PrintPreview from "./PrintPreview";

const PrintDrawerTitle = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold font-[Raleway] text-lg">
        {t("printTemplate")}
      </span>
      <span className="font-normal font-[Raleway] text-sm">
        {t("printSheetDescription")}
      </span>
    </div>
  );
};

const PrintDrawerFooter = ({
  submitButtonProps,
  formId,
}: Pick<PrintDrawerProps, "submitButtonProps" | "formId">) => {
  const { t } = useTranslation();

  return (
    <CustomDialogFooter className="flex flex-row flex-1 items-center gap-4">
      <SheetClose className="flex flex-1">
        <Button
          type="button"
          variant="outline"
          className="flex flex-1 bg-white self-end place-self-end"
        >
          {t("actionCancel")}
        </Button>
      </SheetClose>

      <Button
        {...submitButtonProps}
        type="submit"
        className={cn("flex flex-1", submitButtonProps?.className)}
        form={formId}
        disabled={submitButtonProps?.disabled || submitButtonProps?.isLoading}
      >
        {submitButtonProps?.isLoading ? (
          <Loader2 className="animate-spin" />
        ) : (
          t("overview")
        )}
      </Button>
    </CustomDialogFooter>
  );
};

type PrintDrawerProps = PropsWithChildren<{
  formId: string;
  submitButtonProps?: ButtonProps & { isLoading?: boolean };
  previewUrl: string | null;
  setPreviewUrl: (url: string | null) => void;
  hideText?: boolean;
  iconSize?: number;
  iconColor?: string;
}>;

const PrintDrawer = ({
  children,
  formId,
  submitButtonProps,
  previewUrl,
  setPreviewUrl,
  hideText,
  iconSize,
  iconColor,
}: PrintDrawerProps) => {
  return (
    <StyledDrawer
      title={<PrintDrawerTitle />}
      trigger={
        <PrintButton
          hideText={hideText}
          iconSize={iconSize}
          iconColor={iconColor}
        />
      }
      footer={
        <PrintDrawerFooter
          submitButtonProps={submitButtonProps}
          formId={formId}
        />
      }
      className="min-w-[700px] max-w-[90%]"
    >
      <div>{children}</div>

      <PrintPreview
        fileURL={previewUrl}
        open={previewUrl !== null}
        onOpenChange={() => setPreviewUrl(null)}
      />
    </StyledDrawer>
  );
};

export default PrintDrawer;
